import { useMemo } from "react";
import { ChartTooltipState } from "../../../types";

type Props = {
  tooltipHandler: (tooltip: ChartTooltipState) => void;
  exponent: number;
  onTickWidth: (width: number) => void;
};

type YAxis = {
  x: number;
  y: number;
  payload: { value: number };
};

export const CustomTick = ({
  tooltipHandler,
  exponent,
  onTickWidth,
  ...props
}: Props) => {
  const { x, y, payload } = props as YAxis;

  const MAX_WIDTH = 65;

  const getFont = () => {
    const div = document.querySelector("#root");
    const style = window.getComputedStyle(div!);
    return style.getPropertyValue("font");
  };

  // Measure text width
  const getTextWidth = (text: string | number) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return 0;
    context.font = getFont();
    return context.measureText(text.toString()).width;
  };

  const formattedValue = useMemo(() => {
    const multiplier = Math.pow(10, exponent);
    const finalValue = payload.value / multiplier;

    // First, convert scientific notation to a number safely
    const num = Number(finalValue);

    // If value is not a number, return as-is
    if (isNaN(num)) return finalValue;

    // If it's an integer (up to floating point tolerance) ex: 2.000000004 = 4e-9 (will be rounded to 2 because 1e-8 is greated than 4e-9)
    const rounded = Math.round(num);
    if (Math.abs(num - rounded) < 1e-8) {
      return String(rounded);
    }

    // Otherwise, show up to 2 decimals (removes trailing zero)
    return num.toFixed(2).replace(/\.?0+$/, "");
  }, [exponent, payload]);

  //  Measure and report text width and ellipsize text if it is bigger than max width
  const displayText = useMemo(() => {
    const width = getTextWidth(formattedValue);
    const shouldEllipsize = width > MAX_WIDTH;
    const finalText = shouldEllipsize
      ? formattedValue.toString().slice(0, 6) + "…"
      : formattedValue;
    const finalTextWidth = getTextWidth(finalText);
    onTickWidth(finalTextWidth);
    return finalText;
  }, [formattedValue, onTickWidth]);

  const onMouseEnterHandler = () => {
    tooltipHandler({
      visible: true,
      value: formattedValue,
      x: x - 30, // Adjust position
      y: y,
    });
  };

  // if value is zero zero then dont return anything
  if (payload.value === 0) return null;

  return (
    <text
      {...props}
      dy={6}
      onMouseEnter={onMouseEnterHandler}
      onMouseLeave={() => tooltipHandler({ visible: false })}
    >
      {displayText}
    </text>
  );
};
